import React, { useState } from "react";
import { gql, useMutation } from "@apollo/client";
import { Lock, Loader, Eye, EyeOff, Check } from "lucide-react";
import { validatePassword } from "@/utils/passwordValidation";

const CHANGE_PASSWORD = gql`
  mutation ChangePassword($password: String!) {
    changePassword(password: $password) {
      message
      responseCode
      responseID
      extra
    }
  }
`;

export const ChangePasswordComponent = () => {
  const [state, setState] = useState({
    newPassword: "",
    confirmPassword: "",
    loading: false,
    message: "",
    success: false,
    showPassword: false,
    showConfirmPassword: false
  });

  const [changePassword] = useMutation(CHANGE_PASSWORD);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (state.newPassword !== state.confirmPassword) {
      setState({ ...state, message: "Passwords do not match", success: false });
      return;
    }

    const validation = validatePassword(state.newPassword);
    if (!validation.valid) {
      setState({ 
        ...state, 
        message: validation.errors.join(". "), 
        success: false 
      });
      return;
    }

    setState({ ...state, loading: true, message: "" });

    try {
      const result = await changePassword({
        variables: { password: state.newPassword }
      });

      if (result.data?.changePassword?.responseCode === "200") {
        setState({
          ...state,
          loading: false,
          success: true,
          message: "Password changed successfully!",
          newPassword: "",
          confirmPassword: ""
        });
      } else {
        setState({
          ...state,
          loading: false,
          success: false,
          message: result.data?.changePassword?.message || "Failed to change password"
        });
      }
    } catch (error: any) {
      setState({
        ...state,
        loading: false,
        success: false,
        message: error.message || "An error occurred"
      });
    }
  };

  return (
    <div className="max-w-md">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            New Password
          </label>
          <div className="relative">
            <div className="flex items-center border border-gray-300 rounded-md">
              <Lock className="h-4 w-4 text-gray-400 mx-3" />
              <input
                type={state.showPassword ? "text" : "password"}
                value={state.newPassword}
                onChange={(e) => setState({ ...state, newPassword: e.target.value })}
                className="w-full py-2 pr-10 border-0 focus:ring-0 text-sm"
                placeholder="Enter new password"
                required
              />
              <button
                type="button"
                onClick={() => setState({ ...state, showPassword: !state.showPassword })}
                className="absolute right-3 p-1 text-gray-400 hover:text-gray-600"
                aria-label={state.showPassword ? "Hide new password" : "Show new password"}
                aria-pressed={state.showPassword}
              >
                {state.showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Confirm Password
          </label>
          <div className="relative">
            <div className="flex items-center border border-gray-300 rounded-md">
              <Lock className="h-4 w-4 text-gray-400 mx-3" />
              <input
                type={state.showConfirmPassword ? "text" : "password"}
                value={state.confirmPassword}
                onChange={(e) => setState({ ...state, confirmPassword: e.target.value })}
                className="w-full py-2 pr-10 border-0 focus:ring-0 text-sm"
                placeholder="Confirm new password"
                required
              />
              <button
                type="button"
                onClick={() => setState({ ...state, showConfirmPassword: !state.showConfirmPassword })}
                className="absolute right-3 p-1 text-gray-400 hover:text-gray-600"
                aria-label={state.showConfirmPassword ? "Hide confirm password" : "Show confirm password"}
                aria-pressed={state.showConfirmPassword}
              >
                {state.showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
        </div>

        <div className="text-xs text-gray-600 space-y-1">
          <p>Password requirements:</p>
          <ul className="list-disc list-inside space-y-1">
            <li>At least 8 characters long</li>
            <li>At least one number</li>
            <li>At least one symbol</li>
          </ul>
        </div>

        {state.message && (
          <div className={`text-sm p-3 rounded ${
            state.success 
              ? 'bg-green-50 text-green-800 border border-green-200' 
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}>
            {state.success && <Check className="h-4 w-4 inline mr-2" />}
            {state.message}
          </div>
        )}

        <button
          type="submit"
          disabled={state.loading || !state.newPassword || !state.confirmPassword}
          className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
        >
          {state.loading ? (
            <Loader className="h-5 w-5 animate-spin" />
          ) : (
            "Change Password"
          )}
        </button>
      </form>
    </div>
  );
};
